import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/components/ui/select";
import { Plus, LayoutList, LayoutGrid, X } from "lucide-react";
import React from "react";
import { LostItem } from "@/types";

const LOCATION_OPTIONS = [
  { value: "Pool", label: "Pool" },
  { value: "Clubhouse", label: "Clubhouse" },
  { value: "Street", label: "Street" },
  { value: "Barbecue", label: "Barbecue" },
];

const STATUS_OPTIONS = [
  { value: "All Statuses", label: "All Statuses" },
  { value: "Pending", label: "Pending" },
  { value: "Delivered", label: "Delivered" },
  { value: "Discarded", label: "Discarded" },
  { value: "Deleted", label: "Deleted" },
];

interface LostFoundFiltersProps {
  searchQuery: string;
  location: string;
  status: string;
  view: "list" | "grid";
  onSearch: (query: string) => void;
  onLocationChange: (loc: string) => void;
  onStatusChange: (status: string) => void;
  onReset: () => void;
  onToggleView: (view: "list" | "grid") => void;
  onNew: () => void;
  isMobile: boolean;
  items: LostItem[]; // filtered items
  allItems: LostItem[]; // unfiltered items for counts
}

function countByLocation(items: LostItem[]) {
  const counts: Record<string, number> = {};
  for (const opt of LOCATION_OPTIONS) {
    counts[opt.value] = 0;
  }
  for (const item of items) {
    const loc = (item as any).location;
    if (loc && LOCATION_OPTIONS.some((o) => o.value === loc)) {
      counts[loc] = (counts[loc] || 0) + 1;
    }
  }
  counts["All Locations"] = items.length;
  return counts;
}

function countByStatus(items: LostItem[]) {
  const counts: Record<string, number> = {
    "Pending": 0,
    "Delivered": 0,
    "Discarded": 0,
    "Deleted": 0,
  };
  for (const item of items) {
    if (counts[item.status] !== undefined) {
      counts[item.status]++;
    }
  }
  counts["All Statuses"] = items.length;
  return counts;
}

const LostFoundFilters: React.FC<LostFoundFiltersProps> = ({
  searchQuery,
  location,
  status,
  view,
  onSearch,
  onLocationChange,
  onStatusChange,
  onReset,
  onToggleView,
  onNew,
  isMobile,
  items,
  allItems,
}) => {
  const filterLayout = isMobile
    ? "flex-col gap-2"
    : "flex-row items-center space-x-2";
  const locationCounts = countByLocation(allItems);
  const statusCounts = countByStatus(allItems);

  return (
    <div className={`flex flex-col gap-2 mb-4`}>
      {/* Upper row: Search, Location, Status, Clear Filters */}
      <div className={`flex w-full flex-wrap gap-2`}>
        <Input
          placeholder="Search items..."
          className="max-w-sm"
          value={searchQuery}
          onChange={(e) => onSearch(e.target.value)}
        />
        <div className="w-[192px] min-w-[192px] max-w-xs">
          <Select value={location || "All Locations"} onValueChange={onLocationChange}>
            <SelectTrigger className="w-full whitespace-normal break-words">
              <SelectValue placeholder="Location" />
            </SelectTrigger>
            <SelectContent className="w-[192px] whitespace-normal break-words z-50">
              <SelectItem value="All Locations" className="flex justify-between">
                <span className="whitespace-normal break-words">All Locations</span>
                <span className="ml-2 text-xs text-gray-400">{locationCounts["All Locations"]}</span>
              </SelectItem>
              {LOCATION_OPTIONS.map((opt) => (
                <SelectItem key={opt.value} value={opt.value} className="flex justify-between">
                  <span className="whitespace-normal break-words">{opt.label}</span>
                  <span className="ml-2 text-xs text-gray-400">{locationCounts[opt.value]}</span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="w-[192px] min-w-[192px] max-w-xs">
          <Select value={status || "All Statuses"} onValueChange={onStatusChange}>
            <SelectTrigger className="w-full whitespace-normal break-words">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent className="w-[192px] whitespace-normal break-words z-50">
              {STATUS_OPTIONS.map((opt) => (
                <SelectItem key={opt.value} value={opt.value} className="flex justify-between">
                  <span className="whitespace-normal break-words">{opt.label}</span>
                  <span className="ml-2 text-xs text-gray-400">{statusCounts[opt.value]}</span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <Button variant="outline" onClick={onReset}>
          <X className="mr-2 h-4 w-4" />
          Clear Filters
        </Button>
      </div>
      {/* Lower row: List/Grid, Generate Report, New Lost Item */}
      <div className="flex w-full items-center gap-2">
        <div className="bg-secondary rounded-lg p-1 flex">
          <Button
            variant={view === "list" ? "default" : "ghost"}
            size="sm"
            className="px-2"
            onClick={() => view !== "list" && onToggleView("list")}
          >
            <LayoutList size={16} />
            <span className="ml-1">List</span>
          </Button>
          <Button
            variant={view === "grid" ? "default" : "ghost"}
            size="sm"
            className="px-2"
            onClick={() => view !== "grid" && onToggleView("grid")}
          >
            <LayoutGrid size={16} />
            <span className="ml-1">Grid</span>
          </Button>
        </div>
        {/* Placeholders for Generate Report if needed */}
        <Button className="flex items-center" onClick={onNew}>
          <Plus className="mr-2 h-4 w-4" />
          New Lost Item
        </Button>
      </div>
    </div>
  );
};

export default LostFoundFilters;
